import { createFileRoute } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Image as ImageIcon } from "lucide-react";

export const Route = createFileRoute("/admin/transactions")({ component: TxnAdmin });

type TypeFilter = "all" | "add" | "withdraw" | "win" | "bet" | "bonus" | "reject";
type StatusFilter = "all" | "pending" | "success" | "rejected";

function TxnAdmin() {
  const qc = useQueryClient();
  const [typeF, setTypeF] = useState<TypeFilter>("all");
  const [statusF, setStatusF] = useState<StatusFilter>("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [preview, setPreview] = useState<string | null>(null);

  const { data: txns } = useQuery({
    queryKey: ["all_txns_full"],
    queryFn: async () =>
      (
        await supabase
          .from("wallet_transactions")
          .select("*, profiles(name, phone, wallet_balance)")
          .order("created_at", { ascending: false })
          .limit(500)
      ).data ?? [],
  });

  // Collect admin ids for approver info
  const adminIds = useMemo(() => {
    const s = new Set<string>();
    (txns ?? []).forEach((t: any) => {
      if (t.meta?.approved_by) s.add(t.meta.approved_by);
      if (t.meta?.rejected_by) s.add(t.meta.rejected_by);
    });
    return Array.from(s);
  }, [txns]);

  const { data: admins } = useQuery({
    queryKey: ["admins_lookup", adminIds.join(",")],
    enabled: adminIds.length > 0,
    queryFn: async () => {
      const [{ data: profs }, { data: roles }] = await Promise.all([
        supabase.from("profiles").select("id, name, phone").in("id", adminIds),
        supabase.from("user_roles").select("user_id, role").in("user_id", adminIds),
      ]);
      const roleMap: Record<string, string> = {};
      (roles ?? []).forEach((r: any) => {
        if (r.role === "admin") roleMap[r.user_id] = "Main Admin";
        else if (r.role === "sub_admin" && !roleMap[r.user_id]) roleMap[r.user_id] = "Sub Admin";
      });
      const map: Record<string, { name: string; phone: string; role: string }> = {};
      (profs ?? []).forEach((p: any) => {
        map[p.id] = { name: p.name ?? "—", phone: p.phone ?? "—", role: roleMap[p.id] ?? "Admin" };
      });
      return map;
    },
  });

  // Games lookup for bet transactions (market name)
  const gameIds = useMemo(() => {
    const s = new Set<string>();
    (txns ?? []).forEach((t: any) => {
      if (t.type === "bet" && t.meta?.game_id) s.add(t.meta.game_id);
    });
    return Array.from(s);
  }, [txns]);

  const { data: games } = useQuery({
    queryKey: ["games_lookup_txn", gameIds.join(",")],
    enabled: gameIds.length > 0,
    queryFn: async () => {
      const { data } = await supabase.from("games").select("id, name").in("id", gameIds);
      const map: Record<string, string> = {};
      (data ?? []).forEach((g: any) => { map[g.id] = g.name; });
      return map;
    },
  });

  const marketName = (t: any): string => {
    if (t.type !== "bet") return "";
    const gid = t.meta?.game_id;
    return (gid && games?.[gid]) || "Market unavailable";
  };

  const filtered = useMemo(() => {
    return (txns ?? []).filter((t: any) => {
      // Type filter — "reject" means any rejected txn
      if (typeF === "reject") {
        if (t.status !== "rejected") return false;
      } else if (typeF !== "all") {
        if (typeF === "bonus") {
          if (!["referral_bonus", "bonus"].includes(t.type)) return false;
        } else if (t.type !== typeF) return false;
      }
      if (statusF !== "all" && t.status !== statusF) return false;
      if (from && new Date(t.created_at) < new Date(from)) return false;
      if (to && new Date(t.created_at) > new Date(to + "T23:59:59")) return false;
      return true;
    });
  }, [txns, typeF, statusF, from, to]);

  const summary = useMemo(() => {
    const s = { add: 0, withdraw: 0, win: 0, bet: 0, pending: 0, approved: 0, rejected: 0 };
    filtered.forEach((t: any) => {
      const amt = Number(t.amount || 0);
      if (t.type === "add" && t.status === "success") s.add += amt;
      if (t.type === "withdraw" && t.status === "success") s.withdraw += amt;
      if (t.type === "win" && t.status === "success") s.win += amt;
      if (t.type === "bet" && t.status === "success") s.bet += amt;
      if (t.status === "pending") s.pending += 1;
      if (t.status === "success") s.approved += 1;
      if (t.status === "rejected") s.rejected += 1;
    });
    return s;
  }, [filtered]);

  async function setStatus(t: any, status: "success" | "rejected") {
    const { data: authData } = await supabase.auth.getUser();
    const adminId = authData?.user?.id ?? null;
    const nowIso = new Date().toISOString();
    const nextMeta = {
      ...(t.meta ?? {}),
      ...(status === "success" ? { approved_by: adminId, approved_at: nowIso } : { rejected_by: adminId, rejected_at: nowIso }),
    };
    const { error } = await supabase.from("wallet_transactions").update({ status, meta: nextMeta }).eq("id", t.id);
    if (error) return toast.error(error.message);
    if (status === "success") {
      const delta = ["add", "win", "refund", "referral_bonus"].includes(t.type) ? Number(t.amount) : -Number(t.amount);
      const newBal = Number(t.profiles?.wallet_balance ?? 0) + delta;
      await supabase.from("profiles").update({ wallet_balance: newBal }).eq("id", t.user_id);
    }
    toast.success(`Marked ${status}`);
    qc.invalidateQueries();
  }

  async function openProof(path: string) {
    const { data } = await supabase.storage.from("payment-proofs").createSignedUrl(path, 300);
    if (data?.signedUrl) setPreview(data.signedUrl);
    else toast.error("Could not open screenshot");
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-black">Wallet Transactions</h1>

      {/* Filters */}
      <div className="rounded-2xl bg-card p-3 shadow-card">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          <label className="text-xs">
            <span className="mb-1 block font-semibold text-muted-foreground">From</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs">
            <span className="mb-1 block font-semibold text-muted-foreground">To</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm" />
          </label>
          <label className="text-xs">
            <span className="mb-1 block font-semibold text-muted-foreground">Type</span>
            <select value={typeF} onChange={(e) => setTypeF(e.target.value as TypeFilter)} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm">
              <option value="all">All</option>
              <option value="add">Add Money</option>
              <option value="withdraw">Withdraw</option>
              <option value="win">Win</option>
              <option value="bet">Bet</option>
              <option value="bonus">Bonus</option>
              <option value="reject">Reject</option>
            </select>
          </label>
          <label className="text-xs">
            <span className="mb-1 block font-semibold text-muted-foreground">Status</span>
            <select value={statusF} onChange={(e) => setStatusF(e.target.value as StatusFilter)} className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm">
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="success">Success / Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </label>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6">
        <SumCard label="Add Money" value={`₹${summary.add.toLocaleString("en-IN")}`} tone="success" />
        <SumCard label="Withdraw" value={`₹${summary.withdraw.toLocaleString("en-IN")}`} tone="destructive" />
        <SumCard label="Win" value={`₹${summary.win.toLocaleString("en-IN")}`} tone="primary" />
        <SumCard label="Bet" value={`₹${summary.bet.toLocaleString("en-IN")}`} tone="warning" />
        <SumCard label="Pending" value={summary.pending} tone="warning" />
        <SumCard label="Approved / Rejected" value={`${summary.approved} / ${summary.rejected}`} tone="primary" />
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-x-auto rounded-2xl bg-card shadow-card md:block">
        <table data-sticky-col className="w-full min-w-[1100px] text-sm whitespace-nowrap">
          <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-3">Type</th>
              <th className="px-3 py-3">User</th>
              <th className="px-3 py-3">Amount</th>
              <th className="px-3 py-3">Status</th>
              <th className="px-3 py-3">Date/Time</th>
              <th className="px-3 py-3">Details / Market</th>
              <th className="px-3 py-3">Approved / Rejected By</th>
              <th className="px-3 py-3">Action Time</th>
              <th className="px-3 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t: any) => {
              const meta = t.meta ?? {};
              const bank = meta.bank ?? {};
              const actorId = meta.approved_by || meta.rejected_by;
              const actor = actorId ? admins?.[actorId] : null;
              const actionAt = meta.approved_at || meta.rejected_at;
              return (
                <tr key={t.id} className="border-t align-top">
                  <td className="px-3 py-3 font-semibold capitalize">{t.type}</td>
                  <td className="px-3 py-3 text-muted-foreground">
                    {t.profiles?.name ?? "—"}
                    <span className="block text-[10px]">{t.profiles?.phone}</span>
                  </td>
                  <td className="px-3 py-3 font-bold">₹{Number(t.amount).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${t.status === "success" ? "bg-success/15 text-success" : t.status === "pending" ? "bg-warning/15 text-warning" : "bg-destructive/15 text-destructive"}`}>
                      {t.status === "success" ? "Approved" : t.status}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("en-IN")}</td>
                  <td className="px-3 py-3 text-xs">
                    {t.type === "bet" ? (
                      <div className="font-semibold">Market: {marketName(t)}</div>
                    ) : (
                      <>
                        {meta.utr && <div>UTR: <span className="font-mono">{meta.utr}</span></div>}
                        {meta.upi_id && <div>UPI: <span className="font-mono">{meta.upi_id}</span></div>}
                        {bank.account_number && <div className="text-muted-foreground">A/C: {bank.account_number} {bank.ifsc ? `(${bank.ifsc})` : ""}</div>}
                        {meta.proof_path && (
                          <button onClick={() => openProof(meta.proof_path)} className="mt-1 inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                            <ImageIcon className="h-3 w-3" /> View
                          </button>
                        )}
                        {!meta.utr && !meta.upi_id && !bank.account_number && !meta.proof_path && <span className="text-muted-foreground">—</span>}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs">
                    {actor ? (
                      <>
                        <div className="font-semibold">{actor.name}</div>
                        <div className="text-muted-foreground">{actor.phone}</div>
                        <div className="text-[10px] font-bold uppercase text-primary">{actor.role}</div>
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">{actionAt ? new Date(actionAt).toLocaleString("en-IN") : "—"}</td>
                  <td className="px-3 py-3">
                    {t.status === "pending" && (t.type === "add" || t.type === "withdraw") && (
                      <div className="flex gap-1">
                        <button onClick={() => setStatus(t, "success")} className="rounded bg-success px-2 py-1 text-[10px] font-bold text-success-foreground">Approve</button>
                        <button onClick={() => setStatus(t, "rejected")} className="rounded bg-destructive px-2 py-1 text-[10px] font-bold text-destructive-foreground">Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-muted-foreground">No transactions.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-2 md:hidden">
        {filtered.map((t: any) => {
          const meta = t.meta ?? {};
          const bank = meta.bank ?? {};
          const actorId = meta.approved_by || meta.rejected_by;
          const actor = actorId ? admins?.[actorId] : null;
          const actionAt = meta.approved_at || meta.rejected_at;
          return (
            <div key={t.id} className="rounded-2xl bg-card p-3 shadow-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase text-primary">{t.type}</p>
                  <p className="text-sm font-bold">{t.profiles?.name ?? "—"}</p>
                  <p className="text-[11px] text-muted-foreground">{t.profiles?.phone}</p>
                </div>
                <div className="text-right">
                  <p className="text-base font-black">₹{Number(t.amount).toLocaleString("en-IN")}</p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${t.status === "success" ? "bg-success/15 text-success" : t.status === "pending" ? "bg-warning/15 text-warning" : "bg-destructive/15 text-destructive"}`}>
                    {t.status === "success" ? "Approved" : t.status}
                  </span>
                </div>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1 text-[11px]">
                <div className="text-muted-foreground">Date: {new Date(t.created_at).toLocaleString("en-IN")}</div>
                {t.type === "bet" ? (
                  <div className="col-span-2 font-semibold">Market: {marketName(t)}</div>
                ) : (
                  <>
                    {meta.utr && <div>UTR: <span className="font-mono">{meta.utr}</span></div>}
                    {meta.upi_id && <div>UPI: <span className="font-mono">{meta.upi_id}</span></div>}
                    {bank.account_number && <div className="col-span-2 text-muted-foreground">A/C: {bank.account_number} {bank.ifsc ? `(${bank.ifsc})` : ""}</div>}
                  </>
                )}
                {actor && (
                  <div className="col-span-2">
                    By: <b>{actor.name}</b> ({actor.phone}) — <span className="text-primary">{actor.role}</span>
                    {actionAt && <div className="text-muted-foreground">At: {new Date(actionAt).toLocaleString("en-IN")}</div>}
                  </div>
                )}
              </div>
              <div className="mt-2 flex gap-2">
                {meta.proof_path && (
                  <button onClick={() => openProof(meta.proof_path)} className="inline-flex items-center gap-1 rounded bg-primary/10 px-2 py-1 text-[10px] font-bold text-primary">
                    <ImageIcon className="h-3 w-3" /> Screenshot
                  </button>
                )}
                {t.status === "pending" && (t.type === "add" || t.type === "withdraw") && (
                  <>
                    <button onClick={() => setStatus(t, "success")} className="rounded bg-success px-3 py-1 text-[11px] font-bold text-success-foreground">Approve</button>
                    <button onClick={() => setStatus(t, "rejected")} className="rounded bg-destructive px-3 py-1 text-[11px] font-bold text-destructive-foreground">Reject</button>
                  </>
                )}
              </div>
            </div>
          );
        })}
        {filtered.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">No transactions.</p>}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Payment Screenshot</DialogTitle></DialogHeader>
          {preview && <img src={preview} alt="Payment proof" className="max-h-[70vh] w-full rounded-lg object-contain" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SumCard({ label, value, tone }: { label: string; value: string | number; tone: "warning" | "success" | "destructive" | "primary" }) {
  const cls: Record<string, string> = {
    warning: "bg-warning/10 text-warning",
    success: "bg-success/10 text-success",
    destructive: "bg-destructive/10 text-destructive",
    primary: "bg-primary/10 text-primary",
  };
  return (
    <div className={`rounded-xl p-3 ${cls[tone]}`}>
      <p className="text-[10px] font-bold uppercase tracking-wide opacity-80">{label}</p>
      <p className="mt-1 text-base font-black">{value}</p>
    </div>
  );
}
